import { FileIdsValue, SelectMultipleValue } from '@kaizenplatform/hugot-entity';
import gql from 'graphql-tag';

import GraphQLClient from '../../adapter/GraphQLClient';
import { CreationSetQuery } from '../../graphql/generated/graphql';
import { ArrayElement } from '../../utility/graphql';
import { jsonToDynamicSchemaValue } from '../../utility/graphql/project';
import { DigNonNullable } from '../../utilityTypes/DigNonNullable';

export type CreationSet = NonNullable<CreationSetQuery['creationSet']> & {
  project: CreationSetQuery['creationSet']['project'] & {
    creativeBriefValue?: {
      definition: DigNonNullable<CreationSetQuery, ['creationSet', 'project', 'creativeBriefDefinition']>;
      values: Map<string, string | number | boolean | SelectMultipleValue | FileIdsValue | null>;
    };
    assetCategoryValue?: {
      definition: DigNonNullable<CreationSetQuery, ['creationSet', 'project', 'assetCategoryDefinition']>;
      values: Map<string, string | number | boolean | SelectMultipleValue | FileIdsValue | null>;
    };
  };
};

type CreationsByOptimizerId = CreationSet['creationsByAssignmentId'];
export type CreationByOptimizerId = ArrayElement<CreationsByOptimizerId>;

const singleCreationForDetailFragment = gql`
  fragment SingleCreationForDetail on Creation {
    id
    assignmentId
    state
    proposed
    createdAt
    stateUpdatedAt
    assignee {
      id
      name
      profileImageUrl
    }
    deliverables {
      id
      state
      createdAt
      stateUpdatedAt
      intent
      creative {
        id
        file {
          id
          originalFileName
          url
          size
          mimeType
          fastStart {
            enabled
          }
          attribute {
            aspectRatio {
              size {
                width
                height
              }
            }
            playbackTime {
              seconds
            }
          }
        }
      }
      attachedThumbnail {
        id
        file {
          id
          originalFileName
          url
          mimeType
          size
          fastStart {
            enabled
          }
          attribute {
            aspectRatio {
              size {
                width
                height
              }
            }
            playbackTime {
              seconds
            }
          }
        }
      }
    }
    modificationRequests {
      id
      bucketId
      state
      targetDeliverableId
      feeType
      createdAt
      deliveryDue
      feedback {
        id
        body
        createdAt
      }
      sender {
        id
        name
        profileImageUrl
      }
      items {
        id
        type
        typeName
        assetIds
        body
        metadata
        sender {
          id
          name
          profileImageUrl
        }
        issue {
          id
          name
          resolved
          comments {
            id
            body
            assetIds
            createdAt
            user {
              ... on UserDirector {
                id
                name
                email
                profileImageUrl
              }
              ... on UserOptimizer {
                id
                name
                email
                profileImageUrl
              }
            }
          }
        }
      }
      deliverables {
        id
        createdAt
        creative {
          id
          file {
            id
            originalFileName
            url
            mimeType
            size
          }
        }
        attachedThumbnail {
          id
          file {
            id
            originalFileName
            url
            mimeType
            size
          }
        }
        intent
      }
      offerPricingDetail {
        basicPricings {
          itemType
          actionType
          itemGroupName
          itemGroupKey
          amount
          point
          rewardAmount
          rewardCurrency
        }
        optionPricings {
          itemType
          actionType
          itemGroupName
          itemGroupKey
          amount
          point
          rewardAmount
          rewardCurrency
        }
      }
    }
    creativeFormat {
      id
      network {
        id
        key
        name
      }
      placement {
        id
        key
        name
      }
      creativeType {
        id
        key
        name
      }
      attribute {
        aspectRatio {
          supportingAspectRatioId
          requiredOption {
            name
            amount
            points {
              key
              point
              rewardAmount
              rewardCurrency
            }
          }
          aspectRatio {
            width
            height
          }
          size {
            width
            height
          }
        }
        playbackTime {
          supportingPlaybackTimeId
          requiredOption {
            name
            amount
            points {
              key
              point
              rewardAmount
              rewardCurrency
            }
          }
          seconds
        }
        fastStart {
          enabled
        }
      }
      resizeBaseId
      offerPricingDetails {
        basicPricings {
          itemType
          actionType
          itemGroupName
          itemGroupKey
          amount
          point
          rewardAmount
          rewardCurrency
        }
        optionPricings {
          itemType
          actionType
          itemGroupName
          itemGroupKey
          amount
          point
          rewardAmount
          rewardCurrency
        }
      }
    }
  }
`;

// 既存の流用したので無駄な情報取ってるかもしれない
export const projectForCreationSetDetailFragment = gql`
  fragment ProjectForCreationSetDetail on Project {
    id
    bucketId
    projectType {
      key
      id
      version
      title
      description
      thumbnailImageUrl
      sampleVideoUrl
      expectedDeliveryPeriod
      outputSpecification
      regulation
      creativeBriefDefinition {
        id
        sections {
          id
          label
          description
          meta
          fields {
            ... on DynamicSchemaDefinitionSelectOne {
              key
              type
              label
              choices {
                key
                label
              }
              helperText
            }
            ... on DynamicSchemaDefinitionSelectMultiple {
              key
              type
              label
              choices {
                key
                label
              }
              helperText
              isRequiredToSelectAnything
            }
            ... on DynamicSchemaDefinitionToggle {
              key
              type
              label
              checkedControlLabel
              uncheckedControlLabel
            }
            ... on DynamicSchemaDefinitionSingleLineText {
              key
              type
              label
              placeholder
              helperText
              isRequired
            }
            ... on DynamicSchemaDefinitionMultiLineText {
              key
              type
              label
              placeholder
              helperText
              isRequired
            }
            ... on DynamicSchemaDefinitionPositiveInteger {
              key
              type
              label
              helperText
              isRequired
            }
            ... on DynamicSchemaDefinitionFiles {
              key
              type
              label
              helperText
            }
            ... on DynamicSchemaDefinitionFixed {
              key
              type
              label
              value
            }
          }
        }
      }
      supportingCreativeFormatNetworks {
        id
        key
        name
        placements {
          id
          key
          name
          creativeTypes {
            id
            key
            name
            attribute {
              aspectRatio {
                id
                availables {
                  aspectRatio {
                    width
                    height
                  }
                  presetSize {
                    width
                    height
                  }
                }
              }
              playbackTime {
                id
                availables {
                  seconds
                }
              }
            }
          }
        }
      }
    }
    designInspirationCase {
      id
      name
      description
      url
      thumbnailUrl
    }
    creativeBriefDefinition {
      id
      sections {
        id
        label
        description
        meta
        fields {
          ... on DynamicSchemaDefinitionSelectOne {
            key
            type
            label
            choices {
              key
              label
            }
            helperText
          }
          ... on DynamicSchemaDefinitionSelectMultiple {
            key
            type
            label
            choices {
              key
              label
            }
            helperText
            isRequiredToSelectAnything
          }
          ... on DynamicSchemaDefinitionToggle {
            key
            type
            label
            checkedControlLabel
            uncheckedControlLabel
          }
          ... on DynamicSchemaDefinitionSingleLineText {
            key
            type
            label
            placeholder
            helperText
            isRequired
          }
          ... on DynamicSchemaDefinitionMultiLineText {
            key
            type
            label
            placeholder
            helperText
            isRequired
          }
          ... on DynamicSchemaDefinitionPositiveInteger {
            key
            type
            label
            helperText
            isRequired
          }
          ... on DynamicSchemaDefinitionFiles {
            key
            type
            label
            helperText
          }
          ... on DynamicSchemaDefinitionFixed {
            key
            type
            label
            value
          }
        }
      }
    }
    creativeBriefValues
    assetCategoryDefinition {
      id
      sections {
        id
        meta
        label
        description
        fields {
          ... on DynamicSchemaDefinitionSelectOne {
            key
            type
            label
            choices {
              key
              label
            }
            helperText
          }
          ... on DynamicSchemaDefinitionSingleLineText {
            key
            type
            label
            placeholder
            helperText
            isRequired
          }
          ... on DynamicSchemaDefinitionMultiLineText {
            key
            type
            label
            placeholder
            helperText
            isRequired
          }
          ... on DynamicSchemaDefinitionFileIds {
            key
            type
            label
          }
        }
      }
    }
    assetCategoryValues
    title
    matchingFormat
    organization {
      id
      name
      businessRegion {
        key
      }
    }
    issue {
      id
      name
      comments {
        id
        body
        updatedAt
        user {
          ... on UserDirector {
            id
            name
            email
            profileImageUrl
          }
          ... on UserOptimizer {
            id
            name
            email
            profileImageUrl
          }
        }
        createdAt
        updatedAt
      }
    }
    hasChanges
    canIssueComment
  }
`;

const creationSetForCreationSetDetailFragment = gql`
  ${singleCreationForDetailFragment}
  ${projectForCreationSetDetailFragment}
  fragment CreationSetForCreationSetDetail on CreationSet {
    project {
      ...ProjectForCreationSetDetail
    }
    id
    creationsByAssignmentId {
      ...SingleCreationForDetail
    }
    creationsByOtherAssignedOptimizers {
      ...SingleCreationForDetail
    }
    creativeFormats {
      id
      selectedPricingMerchandiseOptions {
        id
        name
        displayToOptimizer
        helpUrlForOptimizer
      }
      network {
        id
        key
        name
      }
      placement {
        id
        key
        name
      }
      creativeType {
        id
        key
        name
      }
      attribute {
        aspectRatio {
          supportingAspectRatioId
          requiredOption {
            name
            amount
            points {
              key
              point
              rewardAmount
              rewardCurrency
            }
          }
          aspectRatio {
            width
            height
          }
          size {
            width
            height
          }
        }
        playbackTime {
          supportingPlaybackTimeId
          requiredOption {
            name
            amount
            points {
              key
              point
              rewardAmount
              rewardCurrency
            }
          }
          seconds
        }
        fastStart {
          enabled
        }
      }
      resizeBaseId
      offerPricingDetails {
        basicPricings {
          itemType
          actionType
          itemGroupName
          itemGroupKey
          amount
          point
          rewardAmount
          rewardCurrency
        }
        optionPricings {
          itemType
          actionType
          itemGroupName
          itemGroupKey
          amount
          point
          rewardAmount
          rewardCurrency
        }
      }
    }
    amount
    expressDueDays
    currentAssignmentId
  }
`;

class CreationSetWebApi {
  private graphqlClient: GraphQLClient;

  constructor({ graphqlClient }: { graphqlClient: GraphQLClient }) {
    this.graphqlClient = graphqlClient;
  }

  public get = async (args: {
    offerId?: string;
    creationSetId?: string;
    assignmentId: string | null;
  }): Promise<CreationSet> => {
    const { offerId, creationSetId, assignmentId } = args;
    const vars = (() => {
      if (offerId) {
        return { offerId, creationSetId: null, assignmentId };
      } else if (creationSetId) {
        return { offerId: null, creationSetId, assignmentId };
      } else {
        throw Error();
      }
    })();
    const result: CreationSetQuery = (await this.graphqlClient.query(
      gql`
        ${creationSetForCreationSetDetailFragment}
        query creationSet($offerId: ID, $creationSetId: ID, $assignmentId: ID) {
          creationSet(offerId: $offerId, creationSetId: $creationSetId, assignmentId: $assignmentId) {
            ...CreationSetForCreationSetDetail
          }
        }
      `,
      { ...vars },
    )) as CreationSetQuery;

    const cs: CreationSet = result.creationSet;
    if (cs) {
      cs.project.creativeBriefValue = jsonToDynamicSchemaValue(
        cs.project.creativeBriefValues,
        cs.project.creativeBriefDefinition,
      );

      cs.project.assetCategoryValue = jsonToDynamicSchemaValue(
        cs.project.assetCategoryValues,
        cs.project.assetCategoryDefinition,
      );
      return cs;
    } else {
      throw new Error('result.creationSet is blank');
    }
  };
}

export default CreationSetWebApi;
